import { useState } from 'react';
import { motion } from 'framer-motion';
import { PenTool, CheckCircle, AlertCircle, BarChart3, BookOpen, Sparkles } from 'lucide-react';

const sampleCriteria = [
  { id: 'A', name: '知识与理解', max: 8, weight: '评估对学科概念的理解' },
  { id: 'B', name: '应用与分析', max: 8, weight: '运用知识分析问题的能力' },
  { id: 'C', name: '综合与评估', max: 8, weight: '综合信息做出判断的能力' },
  { id: 'D', name: '学术语言', max: 8, weight: '专业术语使用与表达规范' },
];

export default function EssayPage() {
  const [essayText, setEssayText] = useState('');
  const [analyzed, setAnalyzed] = useState(false);

  const wordCount = essayText.trim().split(/\s+/).filter(Boolean).length;
  const charCount = essayText.length;

  const handleAnalyze = () => {
    if (essayText.length > 30) setAnalyzed(true);
  };

  const mockFeedback = {
    scores: [
      { criterion: 'A', score: 6, max: 8, feedback: '知识点覆盖较好，但缺少对"crossing over"的深入讨论' },
      { criterion: 'B', score: 5, max: 8, feedback: '分析逻辑基本清晰，建议增加具体数据支持论点' },
      { criterion: 'C', score: 4, max: 8, feedback: '评估部分较薄弱，需要更多"however"/"on the other hand"等评估性语言' },
      { criterion: 'D', score: 6, max: 8, feedback: '学术语言使用良好，注意统一使用科学术语而非日常用语' },
    ],
    termAnalysis: {
      found: ['cell', 'membrane', 'protein', 'transport', 'phospholipid'],
      missing: ['bilayer', 'integral', 'peripheral', 'cholesterol', 'glycoprotein'],
    },
    suggestions: [
      '增加评估性连接词：however, nevertheless, on the other hand, it could be argued that...',
      '每个论点后添加具体例子或数据支持',
      '结论段需要回应题目要求，不要引入新信息',
      '使用被动语态使表述更学术化：如 "It can be observed that..." 而非 "I think..."',
    ]
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display text-stone-950 tracking-tight">论文编辑器</h1>
        <p className="text-sm text-stone-400 mt-2 font-body">实时反馈，自动对照 IB 评分准则检测论据严密性与学术词汇</p>
      </motion.div>

      <div className="grid lg:grid-cols-5 gap-4 sm:gap-6">
        {/* Editor */}
        <div className="lg:col-span-3 space-y-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium overflow-hidden">
            <div className="px-5 py-3.5 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PenTool className="w-4 h-4 text-stone-950" />
                <span className="text-xs font-semibold text-stone-950 font-body">写作区域</span>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-stone-400 tabular-nums font-body">
                <span>{wordCount} 词</span>
                <span>{charCount} 字符</span>
              </div>
            </div>
            <textarea
              value={essayText}
              onChange={(e) => { setEssayText(e.target.value); setAnalyzed(false); }}
              placeholder={`在此输入你的 IB 论文或论述题答案...\n\n例如：Explain the fluid mosaic model of membrane structure and discuss its significance in understanding cell function.\n\nThe fluid mosaic model, proposed by Singer and Nicolson in 1972, describes the structure of biological membranes...`}
              className="w-full min-h-[250px] sm:min-h-[350px] p-4 sm:p-5 text-sm leading-relaxed outline-none resize-y text-stone-950 font-body placeholder:text-stone-300"
            />
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={handleAnalyze}
            disabled={essayText.length < 30}
            className="w-full py-3.5 rounded-2xl btn-primary text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-body"
          >
            <Sparkles className="w-4 h-4" /> 分析论文
          </motion.button>
        </div>

        {/* Feedback panel */}
        <div className="lg:col-span-2 space-y-4">
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="card-premium p-5">
            <h3 className="text-base font-display text-stone-950 mb-4 flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-steel-50 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-steel" />
              </div>
              评分准则分析
            </h3>
            {sampleCriteria.map((criteria, i) => {
              const score = analyzed ? mockFeedback.scores[i] : null;
              return (
                <div key={criteria.id} className="mb-4 last:mb-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-stone-950 font-body">
                      准则 {criteria.id}: {criteria.name}
                    </span>
                    <span className="text-xs font-bold text-stone-950 tabular-nums font-body">
                      {score ? `${score.score}/${score.max}` : `—/${criteria.max}`}
                    </span>
                  </div>
                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: 'linear-gradient(90deg, var(--sage-dark), var(--sage))' }}
                      initial={{ width: 0 }}
                      animate={{ width: score ? `${(score.score / score.max) * 100}%` : '0%' }}
                      transition={{ duration: 0.7, delay: i * 0.1 }}
                    />
                  </div>
                  {score && <p className="text-[10px] text-stone-400 mt-1.5 font-body">{score.feedback}</p>}
                </div>
              );
            })}
          </motion.div>

          {analyzed && (
            <>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-premium p-5"
              >
                <h3 className="text-base font-display text-stone-950 mb-4 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-cedar-50 flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-cedar" />
                  </div>
                  术语检测
                </h3>
                <div className="mb-3.5">
                  <p className="text-[10px] text-sage-dark font-medium mb-1.5 font-body">✓ 已使用术语</p>
                  <div className="flex flex-wrap gap-1.5">
                    {mockFeedback.termAnalysis.found.map(t => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-md bg-sage-50 text-sage-dark font-body">{t}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-terracotta font-medium mb-1.5 font-body">✗ 建议补充术语</p>
                  <div className="flex flex-wrap gap-1.5">
                    {mockFeedback.termAnalysis.missing.map(t => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-md bg-terracotta-50 text-terracotta font-body">{t}</span>
                    ))}
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card-premium p-5"
              >
                <h3 className="text-base font-display text-stone-950 mb-4 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-cedar-50 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-bronze" />
                  </div>
                  改进建议
                </h3>
                <div className="space-y-2.5">
                  {mockFeedback.suggestions.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-stone-500 font-body">
                      <AlertCircle className="w-3.5 h-3.5 text-bronze mt-0.5 flex-shrink-0" />
                      {s}
                    </div>
                  ))}
                </div>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
